"use client"

import React, { useLayoutEffect, useRef, useState, type CSSProperties } from "react"
import { Button } from "@/components/ui/button"
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronDown, ChevronLeft, ChevronRight, GripVertical, Loader2, Pin } from "lucide-react"
import { useElementSize } from "@/hooks/use-element-size"
import { useExplorerContext } from "@/components/explorer/explorer-context"
import { formatColumnValue, type ExplorerColumn, type ExplorerSort } from "@/lib/explorer-columns"
import { valueAtPath } from "@/lib/record-paths"
import { useTranslation, type Translator } from "@/hooks/use-translation"
import type { TranslationKey } from "@/lib/translations"

export interface RecordsTableProps {
  selectedRowKey: string | null
  onSelectRecord: (record: Record<string, unknown>, index: number) => void
}

function rowKey(record: Record<string, unknown>, index: number): string {
  return record.id === undefined || record.id === null ? `row-${index}` : String(record.id)
}

/**
 * The record list: one column per selected field in the user's order, sticky header,
 * Hub row hover, the selected row highlighted while its blade is open, pager underneath.
 * Headers drag to reorder and click to sort where the ATS can sort, and each header's menu
 * sets the freeze boundary so the leading columns keep their place while the grid scrolls
 * sideways. Empty states explain what blocks a load.
 */
export function RecordsTable({ selectedRowKey, onSelectRecord }: RecordsTableProps) {
  const { page, columns, isLoadingRecords, blocker, pageNumber, nextPage, previousPage, sort, toggleSort, moveSelectedField, selectedFields, frozenColumns, freezeThrough } =
    useExplorerContext()
  const translator = useTranslation()
  const { t, n } = translator
  // The columns are the user's list whenever they have chosen any, and a list can be reordered
  const canReorder = selectedFields.length > 0

  // A frozen cell is offset by the width of the frozen cells before it. offsetLeft would
  // read as the distance to the nearest positioned ancestor rather than to the scroll
  // container, which leaves gaps the scrolled columns show through, so the widths are
  // summed instead. The table sizes to its container, so watch the header row: anything
  // that resizes it — the window, the nav rail collapsing, a blade opening — redistributes
  // the columns, while a change of columns or content is a dependency already.
  const headerCells = useRef<(HTMLTableCellElement | null)[]>([])
  const { ref: headerRowRef, size: headerRowSize } = useElementSize<HTMLTableRowElement>()
  const [offsets, setOffsets] = useState<number[]>([])
  useLayoutEffect(() => {
    const measured: number[] = []
    let left = 0
    for (let index = 0; index < frozenColumns && index < columns.length; index++) {
      measured.push(left)
      left += headerCells.current[index]?.getBoundingClientRect().width ?? 0
    }
    setOffsets((current) => (current.length === measured.length && current.every((value, i) => value === measured[i]) ? current : measured))
  }, [columns, frozenColumns, page, headerRowSize.width])

  const frozenStyle = (index: number): CSSProperties | undefined => (index < frozenColumns ? { left: offsets[index] ?? 0 } : undefined)
  // The boundary column carries the divider that shows where the frozen run ends
  const frozenClass = (index: number): string =>
    index < frozenColumns ? `sticky z-10 ${index === frozenColumns - 1 ? "border-r border-card-border" : ""}` : ""

  const emptyMessage = (() => {
    if (blocker?.kind === "required-filters") return t("explorer.table.blockedRequiredFilters", { names: blocker.names.join(", ") })
    if (blocker?.kind === "no-fields") return t("explorer.table.blockedNoFields")
    if (!page) return t((isLoadingRecords ? "explorer.table.loadingRecords" : "explorer.table.idle"))
    return t("explorer.table.noMatches")
  })()

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 overflow-auto">
        {isLoadingRecords && !page ? (
          <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" /> {t("explorer.table.loadingRecords")}
          </div>
        ) : page && page.records.length > 0 ? (
          <table className="w-full min-w-max caption-bottom text-sm">
            <TableHeader className="sticky top-0 z-20 bg-card shadow-[inset_0_-1px_0_var(--th-card-border)]">
              <TableRow ref={headerRowRef} className="hover:bg-transparent">
                {columns.map((column, index) => (
                  <ColumnHeader
                    key={column.path}
                    ref={(cell) => {
                      headerCells.current[index] = cell
                    }}
                    column={column}
                    sort={sort}
                    onSort={toggleSort}
                    onMove={canReorder ? moveSelectedField : null}
                    onFreeze={freezeThrough}
                    anyFrozen={frozenColumns > 0}
                    className={`${frozenClass(index)} ${index < frozenColumns ? "bg-card" : ""}`}
                    style={frozenStyle(index)}
                    translator={translator}
                  />
                ))}
              </TableRow>
            </TableHeader>
            <TableBody className={isLoadingRecords ? "opacity-60" : ""}>
              {page.records.map((record, index) => {
                const key = rowKey(record, index)
                const selected = key === selectedRowKey
                return (
                  <TableRow
                    key={key}
                    data-state={selected ? "selected" : undefined}
                    className={`cursor-pointer border-card-border bg-card hover:bg-row-hover ${selected ? "bg-accent/60 hover:bg-accent/60" : ""}`}
                    onClick={() => onSelectRecord(record, index)}
                  >
                    {columns.map((column, index) => {
                      const text = formatColumnValue(column.path, valueAtPath(record, column.path), t)
                      return (
                        <TableCell
                          key={column.path}
                          className={`max-w-56 truncate py-2 text-xs ${frozenClass(index)} ${index < frozenColumns ? "bg-inherit" : ""}`}
                          style={frozenStyle(index)}
                          title={text}
                        >
                          {text}
                        </TableCell>
                      )
                    })}
                  </TableRow>
                )
              })}
            </TableBody>
          </table>
        ) : (
          <div className="flex h-40 items-center justify-center px-6 text-center text-sm text-muted-foreground">{emptyMessage}</div>
        )}
      </div>

      <div className="flex items-center justify-between border-t border-card-border px-4 py-2">
        <Button variant="outline" size="sm" className="h-8 rounded-[9px]" onClick={previousPage} disabled={pageNumber <= 1}>
          <ChevronLeft className="mr-1 h-4 w-4" /> {t("explorer.table.previous")}
        </Button>
        <span className="text-xs text-muted-foreground">{t("explorer.table.page", { page: n(pageNumber) })}</span>
        <Button variant="outline" size="sm" className="h-8 rounded-[9px]" onClick={nextPage} disabled={!page?.nextCursor || isLoadingRecords}>
          {t("common.actions.next")} <ChevronRight className="ml-1 h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}

const DRAG_TYPE = "application/x-ats-explorer-column"

interface ColumnHeaderProps {
  column: ExplorerColumn
  sort: ExplorerSort | null
  onSort: (path: string) => void
  /** Null when the columns are not the user's own selection (nothing to reorder) */
  onMove: ((path: string, beforePath: string | null) => void) | null
  /** Freezes through this column, or releases every frozen column when given null */
  onFreeze: (path: string | null) => void
  anyFrozen: boolean
  className?: string
  style?: CSSProperties
  translator: Translator
}

/**
 * One header cell: the label with the parent path beneath it for nested fields, a sort
 * control when the ATS can sort by the field, a drag handle for reordering, and a menu
 * that sets the freeze boundary. It forwards a ref because the grid measures these cells
 * to place the frozen columns.
 */
const ColumnHeader = React.forwardRef<HTMLTableCellElement, ColumnHeaderProps>(function ColumnHeader(
  { column, sort, onSort, onMove, onFreeze, anyFrozen, className, style, translator: { t } },
  ref
) {
  const [isDropTarget, setIsDropTarget] = useState(false)
  const active = sort?.path === column.path
  const SortIcon = active ? (sort?.descending ? ArrowDown : ArrowUp) : ArrowUpDown
  const sortTitle = sortTitleFor(column, active, sort?.descending ?? false, t)

  const readDragged = (event: React.DragEvent) => event.dataTransfer.getData(DRAG_TYPE)

  return (
    <TableHead
      ref={ref}
      className={`th-section-label h-auto whitespace-nowrap py-1.5 ${className ?? ""} ${isDropTarget ? "border-l-2 border-l-[var(--th-active-pill-border)]" : ""}`}
      style={style}
      title={column.path}
      aria-sort={active ? (sort?.descending ? "descending" : "ascending") : undefined}
      onDragOver={(event) => {
        if (!onMove || !event.dataTransfer.types.includes(DRAG_TYPE)) return
        event.preventDefault()
        setIsDropTarget(true)
      }}
      onDragLeave={() => setIsDropTarget(false)}
      onDrop={(event) => {
        setIsDropTarget(false)
        const dragged = readDragged(event)
        if (onMove && dragged && dragged !== column.path) onMove(dragged, column.path)
      }}
    >
      <div className="flex items-center gap-1">
        {onMove && (
          <span
            className="cursor-grab text-muted-foreground/60 hover:text-muted-foreground"
            draggable
            title={t("explorer.table.dragToReorder")}
            onDragStart={(event) => {
              event.dataTransfer.setData(DRAG_TYPE, column.path)
              event.dataTransfer.effectAllowed = "move"
            }}
          >
            <GripVertical className="h-3.5 w-3.5" />
          </span>
        )}
        <button
          type="button"
          className={`flex flex-col items-start text-left ${column.sortable ? "hover:text-foreground" : "cursor-default"}`}
          onClick={() => column.sortable && onSort(column.path)}
          disabled={!column.sortable}
          title={sortTitle}
        >
          <span className="inline-flex items-center gap-1">
            {column.label}
            {column.sortable && <SortIcon className={`h-3 w-3 ${active ? "text-foreground" : "text-muted-foreground/50"}`} />}
          </span>
          {column.detail && <span className="text-[10px] font-normal normal-case tracking-normal text-muted-foreground">{column.detail}</span>}
        </button>
        <DropdownMenu>
          <DropdownMenuTrigger className="ml-auto text-muted-foreground/60 hover:text-muted-foreground" title={t("explorer.table.columnMenu")}>
            <ChevronDown className="h-3.5 w-3.5" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuItem onSelect={() => onFreeze(column.path)}>
              <Pin className="mr-2 h-3.5 w-3.5" /> {t("explorer.table.freezeThrough")}
            </DropdownMenuItem>
            {anyFrozen && <DropdownMenuItem onSelect={() => onFreeze(null)}>{t("explorer.table.unfreezeAll")}</DropdownMenuItem>}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </TableHead>
  )
})

/** The header's tooltip: what a click will do, or why the header is inert */
function sortTitleFor(column: ExplorerColumn, active: boolean, descending: boolean, t: Translator["t"]): string {
  if (!column.sortable) return t("explorer.table.notSortable")
  if (!active) return t("explorer.table.sortBy", { label: column.label })
  return t((descending ? "explorer.table.sortedDescending" : "explorer.table.sortedAscending"))
}

export { rowKey }
